import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import logo from '@/assets/imgs/header/logo.png';
import arrowDown from '@/assets/imgs/home/about/arrow-down.svg';
import arrowLeft from '@/assets/imgs/home/about/arrow-left.svg';
import arrowRight from '@/assets/imgs/home/about/arrow-right.svg';
import balloon from '@/assets/imgs/home/about/balloon.svg';
import channelAirline from '@/assets/imgs/home/about/channel-airline.svg';
import channelBank from '@/assets/imgs/home/about/channel-bank.svg';
import channelOta from '@/assets/imgs/home/about/channel-ota.svg';
import channelSocial from '@/assets/imgs/home/about/channel-social.svg';
import channelTravel from '@/assets/imgs/home/about/channel-travel.svg';
import landscape from '@/assets/imgs/home/about/landscape.png';
import plane from '@/assets/imgs/home/about/plane.png';
import platformRing from '@/assets/imgs/home/about/platform-ring.png';
import providerAirport from '@/assets/imgs/home/about/provider-airport.svg';
import providerCard from '@/assets/imgs/home/about/provider-card.svg';
import providerHotel from '@/assets/imgs/home/about/provider-hotel.svg';
import providerLogistics from '@/assets/imgs/home/about/provider-logistics.svg';
import providerRestaurant from '@/assets/imgs/home/about/provider-restaurant.svg';
import providerRetail from '@/assets/imgs/home/about/provider-retail.svg';
import providerTax from '@/assets/imgs/home/about/provider-tax.svg';
import providerTransport from '@/assets/imgs/home/about/provider-transport.svg';

const channelItems = [
  { icon: channelTravel, key: 'home.aboutUs.channel.travel' },
  { icon: channelAirline, key: 'home.aboutUs.channel.airline' },
  { icon: channelBank, key: 'home.aboutUs.channel.bank' },
  { icon: channelSocial, key: 'home.aboutUs.channel.social' },
  { icon: channelOta, key: 'home.aboutUs.channel.ota' },
];

const providerItems = [
  { icon: providerRestaurant, key: 'home.aboutUs.provider.restaurant' },
  { icon: providerHotel, key: 'home.aboutUs.provider.hotel' },
  { icon: providerRetail, key: 'home.aboutUs.provider.retail' },
  { icon: providerAirport, key: 'home.aboutUs.provider.airport' },
  { icon: providerCard, key: 'home.aboutUs.provider.card' },
  { icon: providerTax, key: 'home.aboutUs.provider.tax' },
  { icon: providerTransport, key: 'home.aboutUs.provider.transport' },
  { icon: providerLogistics, key: 'home.aboutUs.provider.logistics' },
];

const orbitBadges = [
  'left-[249px] top-[7px] [background-position:-249px_-7px]',
  'left-[31px] top-[125px] [background-position:-31px_-125px]',
  'left-[465px] top-[125px] [background-position:-465px_-125px]',
  'left-[40px] top-[376px] [background-position:-40px_-376px]',
  'left-[465px] top-[370px] [background-position:-465px_-370px]',
  'left-[249px] top-[494px] [background-position:-249px_-494px]',
];

const orbitDuration = 18;

const HomeAboutUs = () => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [aboutTitleBeforeBrand = '', aboutTitleAfterBrand = ''] = t('home.aboutUs.title').split('BilionPass');
  const revealViewport = { once: true, amount: 0.25 } as const;
  const revealTransition = { duration: 0.8, ease: [0.22, 1, 0.36, 1] } as const;

  return (
    <section
      className="relative h-[1392px] w-full overflow-hidden bg-[linear-gradient(178.258deg,#e7f4ff_24%,#fff_87%)]"
      aria-labelledby="home-about-us-title"
      data-home-about-us
    >
      <div className="relative left-1/2 h-full w-[1920px] -translate-x-1/2 z-[2]">
      <motion.img
        src={plane}
        alt=""
        className="pointer-events-none absolute left-[1088px] top-[167px] h-[392px] w-[644px] select-none"
        initial={reduceMotion ? false : { opacity: 0, x: 120 }}
        whileInView={reduceMotion ? undefined : { opacity: 1, x: 0 }}
        viewport={revealViewport}
        transition={{ ...revealTransition, duration: 1.1, delay: 0.15 }}
      />
      <motion.img
        src={balloon}
        alt=""
        className="pointer-events-none absolute left-[126px] top-[263px] h-[147px] w-[104px] select-none"
        animate={reduceMotion ? undefined : { y: [0, -14, 0] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
      />

      <motion.div
        className="relative z-10 flex w-full flex-col items-center pt-[88px] text-center"
        initial={reduceMotion ? false : { opacity: 0, y: 48 }}
        whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
        viewport={revealViewport}
        transition={revealTransition}
      >
        <div className="flex items-center justify-center whitespace-nowrap">
          <h2
            id="home-about-us-title"
            className="flex items-center justify-center gap-[10px] font-douyin text-[70px] font-bold leading-none text-[#355f72]"
          >
            <span className="translate-y-[8px]">{aboutTitleBeforeBrand.trim()}</span>
            <img src={logo} alt="BilionPass" className="h-[80px] w-[355px] object-contain" />
            <span className="translate-y-[8px]">{aboutTitleAfterBrand.trim()}</span>
          </h2>
        </div>

        <p className="mt-[34px] w-[1400px] whitespace-pre-line text-[34px] font-normal leading-[1.6] text-[#355f72]">
          {t('home.aboutUs.description')}
        </p>
      </motion.div>

      <motion.div
        className="absolute left-[260px] top-[418px] z-10 h-[737px] w-[344px] overflow-hidden rounded-[23px] bg-[linear-gradient(180deg,#fff4e9_0%,#fff_72%,rgba(255,255,255,0)_100%)]"
        initial={reduceMotion ? false : { opacity: 0, x: -90 }}
        whileInView={reduceMotion ? undefined : { opacity: 1, x: 0 }}
        viewport={revealViewport}
        transition={{ ...revealTransition, delay: 0.12 }}
      >
        <h3 className="flex h-[75px] items-center justify-center rounded-[23px] bg-[#f9780b] font-douyin text-[34px] font-bold text-white shadow-[1px_4px_11px_rgba(0,65,224,0.05)]">
          {t('home.aboutUs.channelTitle')}
        </h3>
        <div className="flex flex-col gap-[26px] px-[33px] pt-[61px]">
          {channelItems.map((item) => (
            <div key={item.key} className="flex min-h-[69px] items-center gap-[26px]">
              <img src={item.icon} alt="" className="size-[69px] shrink-0" />
              <span className="min-w-0 flex-1 text-left text-[27px] font-normal leading-[1.3] text-[#355f72] break-words">
                {t(item.key)}
              </span>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        className="absolute left-[1317px] top-[418px] z-10 h-[737px] min-w-[343px] overflow-hidden rounded-[23px] bg-[linear-gradient(180deg,#f0fbff_19%,#fff_72%,rgba(255,255,255,0)_100%)]"
        initial={reduceMotion ? false : { opacity: 0, x: 90 }}
        whileInView={reduceMotion ? undefined : { opacity: 1, x: 0 }}
        viewport={revealViewport}
        transition={{ ...revealTransition, delay: 0.12 }}
      >
        <h3 className="flex h-[75px] items-center justify-center rounded-[23px] bg-[#01b0ea] font-douyin text-[34px] font-bold text-white shadow-[1px_4px_11px_rgba(0,65,224,0.05)]">
          {t('home.aboutUs.providerTitle')}
        </h3>
        <div className="flex flex-col gap-[3px] px-[55px] pt-[20px]">
          {providerItems.map((item) => (
            <div key={item.key} className="flex min-h-[69px] items-center gap-[26px]">
              <img src={item.icon} alt="" className="size-[59px] shrink-0" />
              <span className="min-w-0 flex-1 text-left text-[27px] font-normal leading-[1.3] text-[#355f72] break-words">
                {t(item.key)}
              </span>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        className="absolute left-[660px] top-[424px] z-10 size-[600px]"
        initial={reduceMotion ? false : { opacity: 0, scale: 0.82 }}
        whileInView={reduceMotion ? undefined : { opacity: 1, scale: 1 }}
        viewport={revealViewport}
        transition={{ ...revealTransition, duration: 0.9, delay: 0.22 }}
      >
        <motion.div
          className="absolute inset-0 size-full"
          animate={reduceMotion ? undefined : { rotate: 360 }}
          transition={{ duration: orbitDuration, repeat: Infinity, ease: 'linear' }}
        >
          <img src={platformRing} alt="" className="absolute inset-0 size-full" />
          {orbitBadges.map((className) => (
            <motion.span
              key={className}
              className={`absolute bg-white rounded-full size-[100px] [background-size:600px_600px] ${className}`}
              style={{ backgroundImage: `url(${platformRing})` }}
              animate={reduceMotion ? undefined : { rotate: -360 }}
              transition={{ duration: orbitDuration, repeat: Infinity, ease: 'linear' }}
            />
          ))}
        </motion.div>
        <div className="absolute left-[152px] top-[152px] flex size-[296px] flex-col items-center justify-center rounded-full bg-white shadow-[0_0_30px_rgba(4,114,248,0.12)]">
          <img src={logo} alt="BilionPass" className="h-[57px] w-[215px] object-contain" />
          <p className="mt-[8px] text-[30px] font-medium leading-none text-[#333] text-center">
            {t('home.aboutUs.platform')}
          </p>
        </div>
      </motion.div>

      <motion.div
        className="absolute left-[595px] top-[680px] z-10 h-[55px] w-[61px]"
        animate={reduceMotion ? undefined : { opacity: [0.45, 1, 0.45], x: [0, 12, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
      >
        <img src={arrowLeft} alt="" className="-rotate-90 size-full" />
      </motion.div>
      <motion.div
        className="absolute left-[1264px] top-[678px] z-10 h-[55px] w-[61px]"
        animate={reduceMotion ? undefined : { opacity: [0.45, 1, 0.45], x: [0, -12, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
      >
        <img src={arrowRight} alt="" className="rotate-90 size-full" />
      </motion.div>
      <motion.div
        className="absolute left-[933px] top-[1020px] z-10 h-[57px] w-[55px]"
        animate={reduceMotion ? undefined : { opacity: [0.45, 1, 0.45], y: [0, 12, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
      >
        <img src={arrowDown} alt="" className="size-full" />
      </motion.div>

      <motion.div
        className="relative z-10 mx-auto mt-[813px] flex h-[75px] w-fit min-w-[514px] items-center justify-center rounded-[89px] bg-[radial-gradient(ellipse_at_center,#00b3e9_0%,#0472f8_100%)] px-[48px]"
        initial={reduceMotion ? false : { opacity: 0, y: 42 }}
        whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
        viewport={revealViewport}
        transition={{ ...revealTransition, delay: 0.35 }}
      >
        <p className="font-douyin text-[34px] font-bold leading-none text-white">
          {t('home.aboutUs.dataLabel')}
        </p>
      </motion.div>
      </div>
      <img
        src={landscape}
        alt=""
        className="pointer-events-none absolute bottom-0 left-0 w-full select-none z-[1]"
      />
    </section>
  );
};

export default HomeAboutUs;
